import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Props as ModalProps } from '@/components/feedback/modal';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import {
  Channel,
  Platform,
  tacticById,
  TacticSelection
} from 'shared/src/domain/line-item/line-item-channels';
import { SearchBar } from '@/components/display/search-bar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarDays,
  faCircleCheck,
  faInfoCircle,
  faLayerGroup,
  faPlusCircle,
  faQuestionCircle,
  faTriangleExclamation
} from '@fortawesome/pro-light-svg-icons';
import { Tooltip } from 'react-tooltip';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  IServerSideDatasource,
  RowClassParams
} from '@ag-grid-community/core';
import { AgGridReact, CustomCellRendererProps, CustomTooltipProps } from '@ag-grid-community/react';
import { defaultColDef } from '@/components/tables/table-utils';
import { client, trpc } from '@/utils/trpc-client';
import { SecondaryButton } from '@/components/display/button';
import {
  getPlatformEntityType,
  getPlatformName
} from 'shared/src/domain/media-platform/media-platforms';
import { PlatformBuyStatus } from '@/features/media-buys/platform-buy-status';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { PlatformBuySearchRow } from 'shared/src/domain/media-buy/media-buy-types-v2';
import { ExecutionLineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { useCaptureMediaPlanCreate } from '@/utils/posthog/analytics';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  lineItem: ExecutionLineItemTrpcResponse;
};

export function PlatformBuysSearchModal({ lineItem, open, setOpen }: Props) {
  const { campaign } = useCampaignContext();
  const { platforms } = useChannelRatesContext();
  const { campaign_number } = campaign;
  const defaultQuery = getDefaultQuery(platforms, campaign_number, lineItem.media_platform_ids);
  const [searchText, setSearchText] = useState<string>(defaultQuery);
  const [addedBuys, setAddedBuys] = useState<string[]>([]);
  const { mutateAsync, isPending } = trpc.createMediaPlan.useMutation();
  const utils = trpc.useUtils();
  const captureCreate = useCaptureMediaPlanCreate();

  async function handleAddMediaPlan(platformBuyIds: string[]) {
    if (isPending || platformBuyIds.length === 0) return;
    const execution_id = lineItem.strategy_id;
    const line_item_id = lineItem.id;

    const allRequests = platformBuyIds.map(media_buy_id =>
      mutateAsync({
        strategy_id: execution_id,
        line_item_id,
        media_plan: { media_buy_id, name: null }
      })
    );
    const created = await Promise.all(allRequests);
    for (const mediaPlan of created) {
      captureCreate({ id: mediaPlan.id, mediaBuyId: mediaPlan.media_buy?.id }, lineItem);
    }
    await utils.invalidate();
  }

  useEffect(() => {
    setSearchText(defaultQuery);
  }, [defaultQuery, open]);

  const handleClose = async () => {
    await handleAddMediaPlan(addedBuys);
    setAddedBuys([]);
  };

  const handleAdd = (platformBuyId: string) => setAddedBuys([...addedBuys, platformBuyId]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      header={<ModalHeader defaultQuery={defaultQuery} onSubmitSearch={setSearchText} />}
      content={
        <PlatformBuysTable
          lineItem={lineItem}
          locallyAddedBuys={addedBuys}
          onPlatformBuyAdd={handleAdd}
          searchText={searchText}
        />
      }
      footer={<ModalFooter setOpen={setOpen} onClose={handleClose} />}
      width="wide"
    />
  );
}

type ModalHeaderProps = {
  defaultQuery: string;
  onSubmitSearch: (text: string) => void;
};

function ModalHeader({ defaultQuery, onSubmitSearch }: ModalHeaderProps) {
  return (
    <div className="flex justify-between gap-3 px-4 pb-2 pt-4">
      <div className="shrink-0 basis-52 text-lg font-semibold">
        Select Platform Buys for this Line Item
      </div>
      <div className="grow rounded-md bg-blue-50 p-1">
        <SearchBar defaultQuery={defaultQuery} submitSearch={onSubmitSearch} />
        <div className="mt-1 flex items-center justify-between text-xs">
          <div className="flex items-start gap-1">
            <span className="shrink-0 font-semibold">Default query:</span>
            <span className="shrink">{defaultQuery}</span>
          </div>
          <SearchTooltip />
        </div>
      </div>
    </div>
  );
}

function ModalFooter({
  onClose,
  setOpen
}: Pick<ModalProps, 'setOpen'> & { onClose: () => Promise<void> }) {
  return (
    <div className="flex justify-between p-4">
      <div className="flex rounded-sm bg-gray-200">
        <div className="flex items-center px-1">
          <FontAwesomeIcon icon={faInfoCircle} />
        </div>
        <div className="rounded bg-gray-100 p-2 text-sm text-gray-800">
          Newly created platform buys are available in Bravo the following morning
        </div>
      </div>
      <SecondaryButton
        onClick={async () => {
          await onClose();
          setOpen(false);
        }}>
        Done
      </SecondaryButton>
    </div>
  );
}

const BottomRow = z.object({
  name: z.string().optional(),
  media_platform_ids: z.string().optional()
});

type TableProps = {
  lineItem: ExecutionLineItemTrpcResponse;
  locallyAddedBuys: string[];
  onPlatformBuyAdd: (platformBuyId: string) => void;
  searchText: string;
};

function PlatformBuysTable({
  lineItem,
  locallyAddedBuys,
  onPlatformBuyAdd,
  searchText
}: TableProps) {
  const { channels, platforms } = useChannelRatesContext();
  const [gridApi, setGridApi] = useState<GridApi<PlatformBuySearchRow> | null>(null);
  const columnDefs = useMemo<ColDef<PlatformBuySearchRow>[]>(
    () => getColumnDefs(channels, platforms, lineItem, onPlatformBuyAdd, locallyAddedBuys),
    [channels, platforms, lineItem, onPlatformBuyAdd, locallyAddedBuys]
  );

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption(
        'serverSideDatasource',
        getServerSideDatasource(lineItem.id, searchText)
      );
    }
  }, [gridApi, lineItem.id, searchText]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
  }, []);

  const getRowStyle = useCallback(
    (params: RowClassParams<PlatformBuySearchRow>) => {
      const { data, node } = params;

      if (!data) return undefined;

      if (node.rowPinned === 'bottom') {
        return {
          backgroundColor: '#f3f4f6' // bg-gray-100
        };
      }

      if (data.availability === 'added' || locallyAddedBuys.includes(data.id)) {
        return {
          backgroundColor: '#eff6ff' // bg-blue-50
        };
      }

      if (data.availability !== 'available') {
        return {
          backgroundColor: '#f9fafb' // bg-gray-50
        };
      }

      return undefined;
    },
    [locallyAddedBuys]
  );

  return (
    <div className="ag-theme-quartz px-4" style={{ height: '100%', width: '100%' }}>
      <AgGridReact
        autoSizeStrategy={{ type: 'fitGridWidth' }}
        cacheBlockSize={50}
        cacheOverflowSize={2}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        infiniteInitialRowCount={100}
        maxBlocksInCache={10}
        onGridReady={onGridReady}
        rowBuffer={0}
        rowModelType="serverSide"
        getRowStyle={getRowStyle}
        suppressCellFocus
        suppressRowHoverHighlight
        tooltipShowDelay={500}
      />
    </div>
  );
}

function SearchTooltip() {
  return (
    <div className="flex shrink-0 items-center justify-end gap-1 text-blue-600">
      <FontAwesomeIcon data-tooltip-id="query-help" icon={faQuestionCircle} />
      <Tooltip
        id="query-help"
        opacity={1}
        place="left"
        style={{ backgroundColor: '#111827', borderRadius: '4px', zIndex: 100000 }}>
        <div className="text-sm">
          <div className="font-semibold">Search Tips</div>
          <ul className="list-disc pl-4">
            <li>Wrap names and phrases with spaces and &lsquo; single quotes in “ ”.</li>
            <li>
              Different criteria must be separated with AND or OR.
              <ul className="list-disc pl-4">
                <li>AND narrows a search (ex. Viant AND Awareness)</li>
                <li>OR broadens a search. (ex. Viant AND Awareness OR Traffic)</li>
              </ul>
            </li>
          </ul>
        </div>
      </Tooltip>
    </div>
  );
}

function getDefaultQuery(
  allPlatforms: Platform[],
  campaignNumber: string,
  platform_ids: number[] | undefined
): string {
  const formattedCampaignNumber = campaignNumber.replace(/\s/g, '');
  const platforms = allPlatforms.filter(p => (platform_ids || []).includes(p.id));
  if (platforms.length === 0) return formattedCampaignNumber;

  const formatedPlatforms = platforms.map(formatPlatformName);
  return platforms.length === 1
    ? `${formattedCampaignNumber} AND ${formatedPlatforms[0]}`
    : `${formattedCampaignNumber} AND (${formatedPlatforms.join(' OR ')})`;
}

function formatPlatformName(platform: Platform): string {
  return platform.name.includes(' ') ? `"${platform.name}"` : platform.name;
}

function getColumnDefs(
  channels: Channel[],
  platforms: Platform[],
  lineItem: ExecutionLineItemTrpcResponse,
  onPlatformBuyAdd: (buy: string) => void,
  locallyAddedBuys: string[]
): ColDef<PlatformBuySearchRow>[] {
  return [
    {
      field: 'name',
      cellStyle: { border: 'none', overflow: 'scroll', textOverflow: 'initial' },
      cellRenderer: NameRenderer,
      headerName: 'Platform Buy',
      maxWidth: 800,
      sortable: false,
      resizable: false,
      suppressMovable: true
    },
    {
      field: 'media_platform_id',
      cellRenderer: MediaPlatformRenderer,
      cellStyle: { border: 'none', overflow: 'scroll', textOverflow: 'initial' },
      headerName: 'Platform Entity',
      pinned: 'right',
      resizable: false,
      sortable: false,
      suppressMovable: true,
      maxWidth: 175
    },
    {
      cellRenderer: CellButtonRenderer,
      cellRendererParams: { onPlatformBuyAdd, locallyAddedBuys },
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        padding: 0,
        border: 'none'
      },
      headerName: '',
      maxWidth: 180,
      pinned: 'right',
      resizable: false,
      sortable: false,
      suppressMovable: true,
      tooltipComponent: (props: CustomTooltipProps & { tactic: TacticSelection }) => (
        <div className="rounded bg-slate-800 px-4 py-1.5 text-sm text-white">{props.value}</div>
      ),
      tooltipValueGetter: params => {
        const { data, node } = params;
        if (!data || node?.rowPinned === 'bottom') return null;
        switch (data.availability) {
          case 'added':
            return null;
          case 'available':
            return null;
          case 'incompatible_platform':
            return (
              <>
                <span className="font-semibold">
                  {getPlatformName(platforms, data.media_platform_id)}
                </span>{' '}
                isn’t a platform option for this line item’s tactic:{' '}
                <span className="font-semibold">
                  {tacticById(channels, lineItem.tactic_id).name})
                </span>
              </>
            );
          case 'invalid_dates':
            return 'This platform buy’s dates don’t overlap the line item’s dates';
          case 'unavailable':
            return 'This platform buy’s overlapping dates are already linked to another line item';
          case 'unavailable_platform':
            return (
              <>
                <span className="font-semibold">
                  {getPlatformName(platforms, data.media_platform_id)}
                </span>{' '}
                isn’t a platform assigned to this line item by the Sr. Media Trader
              </>
            );
        }
      }
    }
  ];
}

function getServerSideDatasource(lineItemId: string, searchText: string): IServerSideDatasource {
  return {
    getRows: async params => {
      if (searchText === '') {
        params.api.setGridOption('pinnedBottomRowData', [
          { name: 'Enter a search query and search again' }
        ]);
        params.success({ rowData: [] });
        return;
      }

      try {
        const { platform_buys, platform_count, total } = await client.searchPlatformBuysV2.query({
          line_item_id: lineItemId,
          page: params.request.endRow ? Math.ceil(params.request.endRow / 50) : 1,
          search: searchText
        });

        if (total == 0) {
          params.api.setGridOption('pinnedBottomRowData', [
            { name: 'No matching platform buys found, try updating your query' }
          ]);
          params.success({ rowData: [] });
          return;
        } else {
          params.api.setGridOption('pinnedBottomRowData', [
            {
              name: `${total.toLocaleString()} Result${total === 1 ? '' : 's'}`,
              media_platform_ids: `${platform_count.toLocaleString()} Platform${platform_count === 1 ? '' : 's'}`
            }
          ]);
          params.success({ rowData: platform_buys });
        }
      } catch (error) {
        console.error('Failed to fetch platform buys', error);
        params.api.setGridOption('pinnedBottomRowData', [
          { name: 'Enter a search query and search again' }
        ]);
        params.success({ rowData: [] });
        return;
      }
    }
  };
}

function NameRenderer({ data, node }: CustomCellRendererProps<PlatformBuySearchRow>) {
  if (!data) return null;
  if (node.rowPinned === 'bottom') {
    const result = BottomRow.safeParse(data);
    return !result.success ? null : result.data.name || '';
  }
  return data.name;
}

function MediaPlatformRenderer({ data, node }: CustomCellRendererProps<PlatformBuySearchRow>) {
  const { platforms } = useChannelRatesContext();
  if (!data) return null;
  if (node.rowPinned === 'bottom') {
    const result = BottomRow.safeParse(data);
    return !result.success ? null : result.data.media_platform_ids || '';
  }
  return `${getPlatformName(platforms, data.media_platform_id)} ${getPlatformEntityType(data.media_platform_id)}`;
}

type CellButtonProps = CustomCellRendererProps<PlatformBuySearchRow> & {
  locallyAddedBuys: string[];
  onPlatformBuyAdd: (buy: string) => void;
};

function CellButtonRenderer({ data, node, locallyAddedBuys, onPlatformBuyAdd }: CellButtonProps) {
  if (!data) return null;

  if (node.rowPinned === 'bottom') {
    return <div className="h-full w-full bg-gray-100" />;
  }

  if (data.availability === 'added' || locallyAddedBuys.includes(data.id)) {
    return (
      <div className="inline-flex h-full w-full items-center pl-1 ">
        <PlatformBuyStatus icon={faCircleCheck} message="Added" theme="blue" />
      </div>
    );
  }

  return (
    <div className="inline-flex h-full w-full items-center pl-1">
      {getAvailabilityCell(data, () => onPlatformBuyAdd(data.id))}
    </div>
  );
}

function getAvailabilityCell(row: PlatformBuySearchRow, onAddClick: () => void) {
  switch (row.availability) {
    case 'incompatible_platform':
      return <PlatformBuyStatus icon={faLayerGroup} theme="red" message="Invalid platform" />;
    case 'unavailable_platform':
      return (
        <PlatformBuyStatus icon={faLayerGroup} theme="yellow" message="Not assigned platform" />
      );
    case 'invalid_dates':
      return <PlatformBuyStatus icon={faCalendarDays} theme="red" message="Invalid dates" />;
    case 'unavailable':
      return (
        <PlatformBuyStatus
          icon={faTriangleExclamation}
          theme="yellow"
          message={
            <Link
              className="underline"
              target="_blank"
              to={`/campaigns/${row.assigned_campaign_id}/execution/lineitems/${row.assigned_line_item_id}/overview`}>
              Dates allocated
            </Link>
          }
        />
      );
    case 'available':
      return (
        <SecondaryButton onClick={onAddClick} className="mr-4 w-full" size="compact">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faPlusCircle} />
            Add
          </div>
        </SecondaryButton>
      );
    case 'added':
      return <PlatformBuyStatus icon={faCircleCheck} message="Added" theme="blue" />;
  }
}
